import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input } from '~/components/Form';
import { MODULO_CUSTOMIZACAO_VLI } from '~/constants/modules';
import { can, t } from '~/helpers';
import { storeData, updateData } from '~/store/modules/pitStopRegra/actions';

import Subitens from './components/Subitens';

function criaSubitemPadrao() {
  return {
    idlocal: Math.random().toString(),
    descricaoobservacaoatendimentoitem: 'Geral',
    statusobservacaoatendimentoitem: 'L',
  };
}

function normalizaSubitem(item = {}) {
  return {
    ...item,
    idlocal:
      item.idlocal ||
      item.idobservacaoatendimentoitem?.toString() ||
      Math.random().toString(),
    descricaoobservacaoatendimentoitem:
      item.descricaoobservacaoatendimentoitem || '',
    statusobservacaoatendimentoitem:
      item.statusobservacaoatendimentoitem || 'L',
  };
}

function preparaSubitensParaEnvio(itens = []) {
  return itens.map(({ idlocal, ...item }) => ({
    idobservacaoatendimentoitem:
      item.idobservacaoatendimentoitem &&
      Number(item.idobservacaoatendimentoitem) > 0
        ? item.idobservacaoatendimentoitem
        : null,
    idobservacaoatendimento: item.idobservacaoatendimento,
    idcliente: item.idcliente,
    descricaoobservacaoatendimentoitem: item.descricaoobservacaoatendimentoitem?.trim(),
    statusobservacaoatendimentoitem: item.statusobservacaoatendimentoitem,
  }));
}

function temSubitemDuplicado(itens = []) {
  const descricoes = itens.map((item) =>
    (item.descricaoobservacaoatendimentoitem || '').trim().toUpperCase()
  );

  return new Set(descricoes).size !== descricoes.length;
}

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();
  const clienteVli = can(MODULO_CUSTOMIZACAO_VLI);

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.pitStopRegra);

  const initials = create ? data : { ...(find || {}), ...data };
  const [subitens, setSubitens] = useState([]);

  const statusOptions = useMemo(
    () => [
      { label: t('s.geral.opcoes.liberado'), value: 'L' },
      { label: t('s.geral.opcoes.bloqueado'), value: 'B' },
      { label: t('s.geral.opcoes.cancelado'), value: 'C' },
    ],
    []
  );

  useEffect(() => {
    if (!clienteVli || loading) {
      return;
    }

    const itensIniciais = initials.itens || [];
    setSubitens(
      itensIniciais.length
        ? itensIniciais.map(normalizaSubitem)
        : [criaSubitemPadrao()]
    );
  }, [clienteVli, loading, create, initials.idobservacaoatendimento]);

  const handleSubmit = useCallback(
    async (param) => {
      try {
        const schema = Yup.object().shape({
          descricaoobservacao: Yup.string()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(100, t('s.geral.validacao.tamanhomaximo').replace('%s', 100)),
        });

        await schema.validate(param, {
          abortEarly: false,
        });

        if (clienteVli && subitens.length === 0) {
          toast.error(t('s.cadastro.pitstop.subitem.obrigatorio'));
          return;
        }

        if (clienteVli && temSubitemDuplicado(subitens)) {
          toast.error(t('s.cadastro.pitstop.subitem.duplicado'));
          return;
        }

        const objSend = {
          ...initials,
          ...param,
          idcliente: client,
          ...(clienteVli ? { itens: preparaSubitensParaEnvio(subitens) } : {}),
        };

        if (create) {
          dispatch(storeData(objSend, params));
        } else {
          dispatch(updateData(objSend, params));
        }
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          reference.current.setErrors(validationErrors);
        }
      }
    },
    [create, client, initials, clienteVli, subitens, params]
  );

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Input
            name="descricaoobservacao"
            label={t('s.cadastro.operacao.descricaooperacao')}
            inputProps={{ maxLength: 100 }}
          />

          {clienteVli && (
            <Subitens
              data={subitens}
              statusOptions={statusOptions}
              onCallback={setSubitens}
            />
          )}
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
